import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  TextField,
  Menu,
  MenuItem,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Popover,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Email as EmailIcon,
  Edit as EditIcon,
  Close as CloseIcon,
  InfoOutlined as InfoOutlinedIcon,
  LockOutlined as LockIcon,
  VisibilityOutlined as RevealIcon,
  Timer as TimerIcon,
  GroupAdd as GroupAddIcon,
  OpenInNew as OpenInNewIcon,
  PictureAsPdfOutlined as PdfIcon,
} from '@mui/icons-material';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import api from '../../services/api';
import { AdminOrderDetail, AdminPaymentDetails, LineItemProvisioningState } from '../../types/order';
import { format } from 'date-fns';
import AdminOrderLineItemsPanel from '../../components/AdminOrderLineItemsPanel';
import OrderActivityLog from '../../components/OrderActivityLog';
import RadiusCredentialsModal from '../../components/RadiusCredentialsModal';
import AccountServiceModal from '../../components/AccountServiceModal';
import StaticIpProvisionModal from '../../components/StaticIpProvisionModal';
import SupaNetworksProvisionModal from '../../components/SupaNetworksProvisionModal';
import CreateAccountFromOrderDialog from '../../components/CreateAccountFromOrderDialog';
import BonusNotice from '../../components/order/BonusNotice';

function formatMoney(price: unknown): string {
  const n = typeof price === 'number' && Number.isFinite(price) ? price : Number(price ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  return `$${safe.toFixed(2)}`;
}

const ORDER_STATUSES = ['pending', 'awaiting_approval', 'approved', 'processing', 'provisioning', 'completed', 'cancelled'] as const;

function getStatusColor(status: string) {
  switch (status) {
    case 'draft': return 'default';
    case 'pending': return 'warning';
    case 'awaiting_approval': return 'warning';
    case 'approved': return 'success';
    case 'processing': return 'info';
    case 'provisioning': return 'primary';
    case 'completed': return 'success';
    case 'cancelled': return 'error';
    default: return 'default';
  }
}

function getStatusLabel(status: string) {
  switch (status) {
    case 'awaiting_approval': return 'Awaiting Approval';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: '#1e3a5c',
        px: 2,
        py: 0.75,
        mx: -3,
        mt: -3,
        mb: 2,
        borderRadius: '4px 4px 0 0',
      }}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
        {children}
      </Typography>
    </Box>
  );
}

function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [order, setOrder] = useState<AdminOrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [paymentDetails, setPaymentDetails] = useState<AdminPaymentDetails | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [paymentCountdown, setPaymentCountdown] = useState(0);
  const paymentTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [provisioningSteps, setProvisioningSteps] = useState<LineItemProvisioningState[]>([]);
  const [activityRefreshKey, setActivityRefreshKey] = useState(0);
  const [radiusModal, setRadiusModal] = useState<{ cartLineIndex: number; existingUsername?: string | null } | null>(null);
  const [staticIpModal, setStaticIpModal] = useState<{ cartLineIndex: number; isComplete: boolean } | null>(null);
  const [accountServiceModal, setAccountServiceModal] = useState<{ cartLineIndex: number; existingAccountServiceId?: string | null } | null>(null);
  const [supaProvisionModal, setSupaProvisionModal] = useState<{ cartLineIndex: number; existingNotes?: string | null } | null>(null);

  const [internalNotes, setInternalNotes] = useState('');
  const [savingMeta, setSavingMeta] = useState(false);

  const [requestedActivationDate, setRequestedActivationDate] = useState('');
  const [savingActivationDate, setSavingActivationDate] = useState(false);
  const [activationDateEditMode, setActivationDateEditMode] = useState(false);

  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [savingStatus, setSavingStatus] = useState(false);

  const [accountEditMode, setAccountEditMode] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState('');
  const [accountSearchResults, setAccountSearchResults] = useState<any[]>([]);
  const [accountSearchLoading, setAccountSearchLoading] = useState(false);
  const accountSearchRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [createAccountDialogOpen, setCreateAccountDialogOpen] = useState(false);

  const [approvalInfoAnchor, setApprovalInfoAnchor] = useState<HTMLElement | null>(null);
  const [approvalReviewUrl, setApprovalReviewUrl] = useState('');
  const [approvalExpiresAt, setApprovalExpiresAt] = useState<string | null>(null);
  const [mintApprovalLoading, setMintApprovalLoading] = useState(false);
  const [lastMintedApprovalToken, setLastMintedApprovalToken] = useState('');
  const [sendApprovalEmailLoading, setSendApprovalEmailLoading] = useState(false);
  const [approvalEmailNotice, setApprovalEmailNotice] = useState<string | null>(null);

  const [confirmationDocs, setConfirmationDocs] = useState<Array<{ id: string; name: string; account_id: string | null }>>([]);
  const [confirmationPdfModal, setConfirmationPdfModal] = useState<{ label: string; src: string } | null>(null);
  const confirmationBlobUrlRef = React.useRef<string | null>(null);

  const loadOrder = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const data = await api.adminGetOrder(id);
      setOrder(data);
      setInternalNotes(data.internal_notes || '');
      setRequestedActivationDate(data.requested_activation_date || '');

      setPaymentDetails(null);

      const provisioningPromise = api.adminGetProvisioningSteps(id)
        .then((res) => setProvisioningSteps(res.line_items))
        .catch(() => setProvisioningSteps([]));

      const confirmationDocsPromise = api.listOrderConfirmationDocuments(id)
        .then((docs) => setConfirmationDocs(docs))
        .catch(() => setConfirmationDocs([]));

      await Promise.all([provisioningPromise, confirmationDocsPromise]);
    } catch (err: any) {
      setError(err.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void loadOrder();
  }, [loadOrder]);

  const saveStatus = async (newStatus: string) => {
    if (!order) return;
    setStatusMenuAnchor(null);
    if (newStatus === order.status) return;
    try {
      setSavingStatus(true);
      setError(null);
      await api.adminUpdateOrder(order.id, { status: newStatus });
      const updated = await api.adminGetOrder(order.id);
      setOrder(updated);
      setActivityRefreshKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const saveAssignAccount = async (accountId: string | null) => {
    if (!order) return;
    try {
      setSavingMeta(true);
      setError(null);
      await api.adminUpdateOrder(order.id, { account_id: accountId });
      const updated = await api.adminGetOrder(order.id);
      setOrder(updated);
      setAccountEditMode(false);
      setAccountSearchQuery('');
      setAccountSearchResults([]);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update assignment');
    } finally {
      setSavingMeta(false);
    }
  };

  const handleAccountSearchChange = (query: string) => {
    setAccountSearchQuery(query);
    if (accountSearchRef.current) clearTimeout(accountSearchRef.current);
    if (!query.trim()) {
      setAccountSearchResults([]);
      return;
    }
    accountSearchRef.current = setTimeout(async () => {
      try {
        setAccountSearchLoading(true);
        const data = await api.getAccounts({ q: query.trim(), per_page: 8 });
        setAccountSearchResults(data.accounts || []);
      } catch {
        setAccountSearchResults([]);
      } finally {
        setAccountSearchLoading(false);
      }
    }, 300);
  };

  const saveInternalNotes = async () => {
    if (!order) return;
    try {
      setSavingMeta(true);
      setError(null);
      await api.adminUpdateOrder(order.id, { internal_notes: internalNotes });
      const updated = await api.adminGetOrder(order.id);
      setOrder(updated);
      setActivityRefreshKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to save notes');
    } finally {
      setSavingMeta(false);
    }
  };

  const saveRequestedActivationDate = async () => {
    if (!order) return;
    try {
      setSavingActivationDate(true);
      setError(null);
      await api.adminUpdateOrder(order.id, { requested_activation_date: requestedActivationDate || null });
      const updated = await api.adminGetOrder(order.id);
      setOrder(updated);
      setActivityRefreshKey((k) => k + 1);
      setActivationDateEditMode(false);
    } catch (err: any) {
      const validationMsg = err.response?.data?.errors?.[0]?.msg;
      setError(validationMsg || err.response?.data?.detail || err.message || 'Failed to save activation date');
    } finally {
      setSavingActivationDate(false);
    }
  };

  const handleToggleStep = async (cartLineIndex: number, stepKey: string) => {
    if (!order) return;
    try {
      const updated = await api.adminToggleProvisioningStep(order.id, cartLineIndex, stepKey);
      setProvisioningSteps((prev) =>
        prev.map((li) => li.cart_line_index === cartLineIndex ? updated : li)
      );
      setActivityRefreshKey((k) => k + 1);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to update provisioning step');
    }
  };

  const handleRadiusStep = (cartLineIndex: number) => {
    const lineItem = provisioningSteps.find((li) => li.cart_line_index === cartLineIndex);
    const step = lineItem?.steps.find((s) => s.key === 'radius_credentials');
    setRadiusModal({
      cartLineIndex,
      existingUsername: step?.is_complete && step.notes ? step.notes : null,
    });
  };

  const handleRadiusSuccess = (updated: LineItemProvisioningState) => {
    setProvisioningSteps((prev) =>
      prev.map((li) => li.cart_line_index === updated.cart_line_index ? updated : li)
    );
    setActivityRefreshKey((k) => k + 1);
  };

  const handleAccountServiceStep = (cartLineIndex: number) => {
    const lineItem = provisioningSteps.find((li) => li.cart_line_index === cartLineIndex);
    const step = lineItem?.steps.find((s) => s.key === 'account_service');
    setAccountServiceModal({
      cartLineIndex,
      existingAccountServiceId: step?.is_complete && step.notes ? step.notes : null,
    });
  };

  const handleStaticIpStep = (cartLineIndex: number) => {
    const lineItem = provisioningSteps.find((li) => li.cart_line_index === cartLineIndex);
    const step = lineItem?.steps.find((s) => s.key === 'assign_ip');
    setStaticIpModal({ cartLineIndex, isComplete: !!step?.is_complete });
  };

  const handleAccountServiceSuccess = (updated: LineItemProvisioningState) => {
    setProvisioningSteps((prev) =>
      prev.map((li) => li.cart_line_index === updated.cart_line_index ? updated : li)
    );
    setActivityRefreshKey((k) => k + 1);
  };

  const handleSupaProvisionStep = (cartLineIndex: number) => {
    const lineItem = provisioningSteps.find((li) => li.cart_line_index === cartLineIndex);
    const step = lineItem?.steps.find((s) => s.key === 'supa_provision');
    setSupaProvisionModal({
      cartLineIndex,
      existingNotes: step?.is_complete ? (step.notes || null) : null,
    });
  };

  const handleSupaProvisionSuccess = (updated: LineItemProvisioningState) => {
    setProvisioningSteps((prev) =>
      prev.map((li) => li.cart_line_index === updated.cart_line_index ? updated : li)
    );
    setActivityRefreshKey((k) => k + 1);
    // Refresh order to pick up updated internal_notes
    void loadOrder();
  };

  const PAYMENT_REVEAL_SECONDS = 60;

  const handleRevealPayment = async () => {
    if (!order) return;
    try {
      setLoadingPayment(true);
      const payment = await api.adminGetPaymentDetails(order.id);
      setPaymentDetails(payment);
      setPaymentCountdown(PAYMENT_REVEAL_SECONDS);
      setActivityRefreshKey((k) => k + 1);
      if (paymentTimerRef.current) clearInterval(paymentTimerRef.current);
      paymentTimerRef.current = setInterval(() => {
        setPaymentCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(paymentTimerRef.current!);
            paymentTimerRef.current = null;
            setPaymentDetails(null);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      setError(err.message || 'Failed to retrieve payment details');
    } finally {
      setLoadingPayment(false);
    }
  };

  const handleDeletePayment = async () => {
    if (!order) return;
    if (!window.confirm('Are you sure you want to delete payment details? This action cannot be undone.')) return;
    try {
      await api.adminDeletePaymentDetails(order.id);
      setPaymentDetails(null);
      if (paymentTimerRef.current) { clearInterval(paymentTimerRef.current); paymentTimerRef.current = null; }
      setPaymentCountdown(0);
      setOrder({ ...order, payment_details_available: false, payment_cvv_on_file: false });
    } catch (err: any) {
      setError(err.message || 'Failed to delete payment details');
    }
  };

  const handleDownloadDocument = async () => {
    if (!order || !order.porting_document_path) return;
    try {
      const blob = await api.adminGetPortingDocument(order.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `porting-document-${order.order_reference}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      setError(err.message || 'Failed to download document');
    }
  };

  const handleMintApprovalLink = async () => {
    if (!order) return;
    try {
      setMintApprovalLoading(true);
      setError(null);
      setApprovalEmailNotice(null);
      const minted = await api.adminMintOrderApprovalLink(order.id);
      const path = minted.review_url_path || '';
      const full =
        minted.customer_review_url ||
        (() => {
          const origin = window.location.origin.replace(/\/$/, '');
          return path.startsWith('http://') || path.startsWith('https://')
            ? path
            : `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
        })();
      setApprovalReviewUrl(full);
      setLastMintedApprovalToken(minted.token || '');
      setApprovalExpiresAt(minted.expires_at || null);
      const updated = await api.adminGetOrder(order.id);
      setOrder(updated);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to generate approval link');
    } finally {
      setMintApprovalLoading(false);
    }
  };

  const handleSendApprovalEmail = async () => {
    if (!order || !lastMintedApprovalToken) return;
    if (!(order.email || '').trim()) {
      setError('This order has no customer email address.');
      return;
    }
    try {
      setSendApprovalEmailLoading(true);
      setError(null);
      setApprovalEmailNotice(null);
      await api.adminSendOrderApprovalEmail(order.id, { approval_token: lastMintedApprovalToken });
      setApprovalEmailNotice(`Approval email sent to ${order.email.trim()}.`);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to send approval email');
    } finally {
      setSendApprovalEmailLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/orders')} sx={{ mb: 2 }}>
          Back to Orders
        </Button>
        <Alert severity="error">{error || 'Order not found.'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumb */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin/orders')} size="small">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer' }} onClick={() => navigate('/admin/orders')}>
          Order Management
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Page header — order ref left, status + approval actions right */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
          {/* Left: title + status */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {order.order_reference}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Click to change status">
                <Chip
                  label={savingStatus ? 'Saving…' : getStatusLabel(order.status)}
                  color={getStatusColor(order.status) as any}
                  onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
                  sx={{ cursor: 'pointer' }}
                />
              </Tooltip>
              {order.customer_accepted_at && (
                <Chip
                  label={`Customer approved ${format(new Date(order.customer_accepted_at), 'dd MMM yyyy')}`}
                  color="success"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={() => setStatusMenuAnchor(null)}
            >
              {ORDER_STATUSES.map((s) => (
                <MenuItem key={s} selected={s === order.status} onClick={() => void saveStatus(s)}>
                  <Chip label={getStatusLabel(s)} color={getStatusColor(s) as any} size="small" sx={{ cursor: 'pointer' }} />
                </MenuItem>
              ))}
            </Menu>
          </Box>

          {/* Right: approval link actions */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1, minWidth: 260 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => void handleMintApprovalLink()}
                disabled={mintApprovalLoading}
              >
                {mintApprovalLoading ? 'Generating…' : 'Generate new approval link'}
              </Button>
              <IconButton size="small" onClick={(e) => setApprovalInfoAnchor(e.currentTarget)}>
                <InfoOutlinedIcon fontSize="small" color="action" />
              </IconButton>
              <Popover
                open={Boolean(approvalInfoAnchor)}
                anchorEl={approvalInfoAnchor}
                onClose={() => setApprovalInfoAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 2, maxWidth: 360 }}>
                  <Typography variant="subtitle2" gutterBottom>Customer approval (magic link)</Typography>
                  <Typography variant="body2" sx={{ mb: 1.5 }}>
                    Generates a one-time link the customer can open without logging in. Configure the customer
                    portal URL and link lifetime under Settings → Ordering → Customer approval. Upload PDF terms
                    under Settings → Ordering → Catalogue terms. Use Send approval email after generating a link
                    to message the customer (template under Settings → Ordering → Approval email).
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No active approval link on file. Generate one to send to the customer.
                  </Typography>
                </Box>
              </Popover>
            </Box>

            {!approvalExpiresAt && order.agreement_token_expires_at && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                Current link expires: {format(new Date(order.agreement_token_expires_at), 'dd MMM yyyy HH:mm')}
              </Typography>
            )}

            {approvalReviewUrl && (
              <TextField
                size="small"
                fullWidth
                label="Customer review URL"
                value={approvalReviewUrl}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => { void navigator.clipboard.writeText(approvalReviewUrl); }}>
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}
            {approvalExpiresAt && (
              <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'right' }}>
                Link expires {format(new Date(approvalExpiresAt), 'dd MMM yyyy HH:mm')}
              </Typography>
            )}
            {approvalReviewUrl && lastMintedApprovalToken && (
              <Button
                variant="contained"
                size="small"
                startIcon={<EmailIcon />}
                onClick={() => void handleSendApprovalEmail()}
                disabled={sendApprovalEmailLoading || !(order.email || '').trim() || mintApprovalLoading}
                sx={{ alignSelf: 'flex-end' }}
              >
                {sendApprovalEmailLoading ? 'Sending…' : 'Send approval email'}
              </Button>
            )}
            {approvalReviewUrl && !(order.email || '').trim() && (
              <Typography variant="caption" color="warning.main">
                Add a customer email before sending the approval email.
              </Typography>
            )}
            {approvalEmailNotice && (
              <Alert severity="success" onClose={() => setApprovalEmailNotice(null)} sx={{ py: 0 }}>
                {approvalEmailNotice}
              </Alert>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Two-column body */}
      <Grid container spacing={2} alignItems="flex-start">

        {/* LEFT — main content */}
        <Grid item xs={12} md={8}>

          {/* Line items */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <SectionLabel>Line items and per-unit configuration</SectionLabel>
            <AdminOrderLineItemsPanel
              orderItems={order.order_items}
              orderId={order.id}
              provisioningSteps={provisioningSteps}
              onToggleStep={handleToggleStep}
              onRadiusStep={handleRadiusStep}
              onAccountServiceStep={handleAccountServiceStep}
              onSupaProvisionStep={handleSupaProvisionStep}
              onStaticIpStep={handleStaticIpStep}
            />
          </Paper>

          {/* Order Summary */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <SectionLabel>Order Summary</SectionLabel>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  Monthly recurring
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700, color: 'primary.main' }}>
                  {formatMoney(order.monthly_total ?? order.total_monthly ?? order.monthly_recurring ?? 0)}
                </Typography>
                <Typography variant="caption" color="text.secondary">per month</Typography>
              </Paper>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.8 }}>
                  One-time total
                </Typography>
                <Typography variant="h5" sx={{ mt: 0.5, fontWeight: 700 }}>
                  {formatMoney(
                    Number(order.setup_total ?? order.setup_fees ?? 0) +
                    Number(order.equipment_total ?? 0) +
                    Number(order.delivery_fee ?? 0),
                  )}
                </Typography>
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  {[
                    { label: 'Setup fees', value: order.setup_total ?? order.setup_fees ?? 0 },
                    { label: 'Equipment', value: order.equipment_total ?? 0 },
                    { label: 'Delivery', value: order.delivery_fee ?? 0 },
                  ].map(({ label, value }) => (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="caption">{formatMoney(value)}</Typography>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Box>
            {order.coupon_code && (
              <Box sx={{ mt: 2, p: 1.5, bgcolor: 'success.lighter', borderRadius: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={700} color="success.dark">
                  Promo applied: {order.coupon_code}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  {order.coupon_discount_amount != null &&
                    `— $${Number(order.coupon_discount_amount).toFixed(2)}/mo off for ${order.coupon_discount_months} months`}
                </Typography>
              </Box>
            )}
            {order.coupon_bonus_title && (
              <Box sx={{ mt: 2 }}>
                <BonusNotice
                  title={order.coupon_bonus_title}
                  description={order.coupon_bonus_description}
                  terms={order.coupon_bonus_terms}
                />
              </Box>
            )}
          </Paper>

          {/* Payment */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <SectionLabel>Payment Information</SectionLabel>
            {order.payment_method ? (
              <>
                <Typography variant="body2" sx={{ mb: 1.5 }}>
                  <strong>Method:</strong>{' '}
                  {order.payment_method === 'credit_card' ? 'Credit Card' : 'Direct Debit'}
                  {order.payment_method_label ? ` — ${order.payment_method_label}` : ''}
                </Typography>
                {order.account_id && order.payment_record_status && (
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                    Held in the account's{' '}
                    <RouterLink to={`/accounts/${order.account_id}?tab=billing&sub=payment-methods`}>Billing › Payment Methods</RouterLink>
                    {' '}— deleting details here deletes them there too.
                  </Typography>
                )}
                {order.payment_cvv_on_file && (
                  <Alert severity="warning" sx={{ mb: 1.5, py: 0.5 }}>CVV on file. Delete it once the details have been processed (it is removed automatically after 7 days).</Alert>
                )}

                {order.payment_details_available ? (
                  paymentDetails ? (
                    <>
                      <Alert
                        severity="warning"
                        icon={<TimerIcon fontSize="inherit" />}
                        sx={{ mb: 2, py: 0.5 }}
                      >
                        Details hidden in {paymentCountdown}s. Every access is logged.
                      </Alert>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mb: 2 }}>
                        {paymentDetails.credit_card && (
                          <>
                            <Typography variant="body2"><strong>Card Number:</strong> {paymentDetails.credit_card.card_number}</Typography>
                            <Typography variant="body2"><strong>Expiry:</strong> {paymentDetails.credit_card.expiry_month}/{paymentDetails.credit_card.expiry_year}</Typography>
                            <Typography variant="body2"><strong>CVV:</strong> {paymentDetails.credit_card.cvv || 'not on file'}</Typography>
                            <Typography variant="body2"><strong>Name on Card:</strong> {paymentDetails.credit_card.name_on_card}</Typography>
                          </>
                        )}
                        {paymentDetails.direct_debit && (
                          <>
                            <Typography variant="body2"><strong>BSB:</strong> {paymentDetails.direct_debit.bsb}</Typography>
                            <Typography variant="body2"><strong>Account Number:</strong> {paymentDetails.direct_debit.account_number}</Typography>
                            <Typography variant="body2"><strong>Account Name:</strong> {paymentDetails.direct_debit.account_name}</Typography>
                          </>
                        )}
                      </Box>
                      {isAdmin && (
                        <Button startIcon={<DeleteIcon />} onClick={handleDeletePayment} color="error" size="small">
                          Delete Payment Details
                        </Button>
                      )}
                    </>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                        <LockIcon fontSize="small" />
                        <Typography variant="body2">Details are encrypted at rest. Access is audit-logged.</Typography>
                      </Box>
                      {isAdmin ? (
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={loadingPayment ? <CircularProgress size={14} /> : <RevealIcon />}
                            onClick={handleRevealPayment}
                            disabled={loadingPayment}
                          >
                            View Payment Details
                          </Button>
                          <Button startIcon={<DeleteIcon />} onClick={handleDeletePayment} color="error" size="small" variant="outlined">
                            Delete
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">Only admins can view or delete payment details.</Typography>
                      )}
                    </Box>
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Payment details have been deleted{order.payment_method_label ? ` (${order.payment_method_label})` : ''}.
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No payment captured in SonOps for this order (external billing or staff-created catalog order).
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* RIGHT — sidebar */}
        <Grid item xs={12} md={4}>

          {/* Account */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <SectionLabel>Account</SectionLabel>
            {!accountEditMode ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: order.account_name ? 500 : 400, color: order.account_name ? 'text.primary' : 'text.secondary' }}
                  >
                    {order.account_name || 'Unassigned'}
                  </Typography>
                  {order.account_number && (
                    <Typography variant="body2" color="text.secondary">
                      #{order.account_number}
                    </Typography>
                  )}
                </Box>
                {order.account_id && (
                  <Tooltip title="Go to account">
                    <IconButton size="small" onClick={() => navigate(`/accounts/${order.account_id}`)}>
                      <OpenInNewIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {!order.account_id && (
                  <Tooltip title="Create account from this order">
                    <IconButton size="small" onClick={() => setCreateAccountDialogOpen(true)}>
                      <GroupAddIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton size="small" onClick={() => { setAccountEditMode(true); setAccountSearchQuery(''); setAccountSearchResults([]); }}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Search for an account to link, or clear the current assignment.
                </Typography>
                <TextField
                  size="small"
                  label="Search accounts"
                  fullWidth
                  autoFocus
                  value={accountSearchQuery}
                  onChange={(e) => handleAccountSearchChange(e.target.value)}
                  placeholder="Type account name…"
                  InputProps={{
                    endAdornment: accountSearchLoading ? (
                      <InputAdornment position="end"><CircularProgress size={16} /></InputAdornment>
                    ) : null,
                  }}
                />
                {accountSearchResults.length > 0 && (
                  <Paper variant="outlined" sx={{ mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                    <List dense disablePadding>
                      {accountSearchResults.map((acc: any) => (
                        <ListItemButton key={acc.id} onClick={() => saveAssignAccount(acc.id)} disabled={savingMeta}>
                          <ListItemText primary={acc.organisation_name} secondary={acc.email || undefined} />
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {order.account_id && (
                    <Button size="small" color="error" variant="outlined" onClick={() => saveAssignAccount(null)} disabled={savingMeta}>
                      Remove
                    </Button>
                  )}
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<CloseIcon />}
                    onClick={() => { setAccountEditMode(false); setAccountSearchQuery(''); setAccountSearchResults([]); }}
                    disabled={savingMeta}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Customer */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <SectionLabel>Customer</SectionLabel>
            <Typography variant="body2"><strong>Name:</strong> {order.first_name} {order.last_name}</Typography>
            <Typography variant="body2"><strong>Email:</strong> {order.email}</Typography>
            <Typography variant="body2"><strong>Mobile:</strong> {order.mobile_number}</Typography>
            {order.date_of_birth && (
              <Typography variant="body2">
                <strong>Date of Birth:</strong>{' '}
                {format(new Date(order.date_of_birth + 'T00:00:00'), 'dd MMM yyyy')}
              </Typography>
            )}
            {order.customer_type === 'business' && (
              <>
                <Divider sx={{ my: 1.5 }} />
                <Typography variant="body2"><strong>Organisation:</strong> {order.organisation_name}</Typography>
                <Typography variant="body2"><strong>ABN/ACN:</strong> {order.abn_acn}</Typography>
              </>
            )}

          </Paper>

          {/* Address */}
          <Paper sx={{ p: 3, mb: 2 }}>
            <SectionLabel>Service Address</SectionLabel>
            {order.service_address.split('; ').map((addr, i) => (
              <Typography key={i} variant="body2" sx={{ display: 'list-item', listStyleType: 'disc', ml: 2 }}>{addr}</Typography>
            ))}
            {order.billing_address && order.billing_address !== order.service_address && (
              <>
                <Box
                  sx={{
                    bgcolor: '#1e3a5c',
                    color: '#ffffff',
                    px: 2,
                    py: 0.75,
                    mx: -3,
                    mt: 2,
                    mb: 2,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: '#ffffff' }}>
                    Billing Address
                  </Typography>
                </Box>
                <Typography variant="body2">{order.billing_address}</Typography>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Requested Activation Date
            </Typography>
            {!activationDateEditMode ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{ flex: 1, color: order.requested_activation_date ? 'text.primary' : 'text.secondary' }}
                >
                  {order.requested_activation_date
                    ? format(new Date(order.requested_activation_date + 'T00:00:00'), 'dd MMM yyyy')
                    : 'Not set'}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => {
                    setRequestedActivationDate(order.requested_activation_date || '');
                    setActivationDateEditMode(true);
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : (
              <Box>
                <TextField
                  type="date"
                  size="small"
                  value={requestedActivationDate}
                  onChange={(e) => setRequestedActivationDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: new Date().toISOString().split('T')[0] }}
                  autoFocus
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={saveRequestedActivationDate}
                    disabled={savingActivationDate}
                  >
                    Save
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<CloseIcon />}
                    onClick={() => setActivationDateEditMode(false)}
                    disabled={savingActivationDate}
                  >
                    Cancel
                  </Button>
                </Box>
              </Box>
            )}
          </Paper>

          {/* Voice */}
          {order.voice_service_selected && (
            <Paper sx={{ p: 3, mb: 2 }}>
              <SectionLabel>Voice Service</SectionLabel>
              <Typography variant="body2"><strong>Channels:</strong> {order.voice_channels}</Typography>
              {order.porting_number && (
                <>
                  <Typography variant="body2"><strong>Porting Number:</strong> {order.porting_number}</Typography>
                  {order.porting_document_path && (
                    <Button startIcon={<DownloadIcon />} onClick={handleDownloadDocument} size="small" sx={{ mt: 1 }}>
                      Download Porting Document
                    </Button>
                  )}
                </>
              )}
            </Paper>
          )}

          {/* Order Confirmation PDF */}
          {confirmationDocs.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <SectionLabel>Order Confirmation</SectionLabel>
              {confirmationDocs.map((doc) => (
                <Box
                  key={doc.id}
                  onClick={async () => {
                    const apiUrl = doc.account_id
                      ? api.accountDocumentFileUrl(doc.account_id, doc.id)
                      : `/api/admin/orders/${id}/confirmation-documents/${doc.id}/file`;
                    if (confirmationBlobUrlRef.current) {
                      URL.revokeObjectURL(confirmationBlobUrlRef.current);
                    }
                    const blobUrl = await api.fetchDocumentBlobUrl(apiUrl);
                    confirmationBlobUrlRef.current = blobUrl;
                    setConfirmationPdfModal({ label: doc.name, src: blobUrl });
                  }}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer',
                    p: 1, borderRadius: 1, '&:hover': { bgcolor: '#f0f4ff' },
                  }}
                >
                  <PdfIcon sx={{ color: '#d32f2f', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ color: '#1976d2', textDecoration: 'underline' }}>
                    {doc.name}
                  </Typography>
                </Box>
              ))}
            </Paper>
          )}

          {/* Timestamps */}
          <Paper sx={{ p: 3 }}>
            <SectionLabel>Record</SectionLabel>
            <Typography variant="body2" color="text.secondary">
              <strong>Created:</strong> {format(new Date(order.created_at), 'PPp')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Updated:</strong> {format(new Date(order.updated_at), 'PPp')}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Full-width bottom */}
      <Paper sx={{ p: 3, mt: 2 }}>
        <SectionLabel>Internal Notes</SectionLabel>
        <TextField
          label="Staff notes (not shown to customer)"
          fullWidth
          multiline
          minRows={2}
          value={internalNotes}
          onChange={(e) => setInternalNotes(e.target.value)}
        />
        <Button sx={{ mt: 1 }} variant="outlined" size="small" onClick={saveInternalNotes} disabled={savingMeta}>
          Save notes
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mt: 2 }}>
        <OrderActivityLog orderId={order.id} refreshKey={activityRefreshKey} />
      </Paper>

      {createAccountDialogOpen && (
        <CreateAccountFromOrderDialog
          open={createAccountDialogOpen}
          order={order}
          onClose={() => setCreateAccountDialogOpen(false)}
          onSuccess={() => {
            setCreateAccountDialogOpen(false);
            void loadOrder();
          }}
        />
      )}

      {radiusModal && (() => {
        const lineItem = provisioningSteps.find(
          (li) => li.cart_line_index === radiusModal.cartLineIndex,
        );
        const supaStep = lineItem?.steps.find((s) => s.key === 'supa_provision');
        let defaultBuid: string | null = null;
        if (supaStep?.is_complete && supaStep.notes) {
          try { defaultBuid = (JSON.parse(supaStep.notes) as { buid?: string }).buid || null; } catch { /* ignore */ }
        }
        return (
          <RadiusCredentialsModal
            open={!!radiusModal}
            orderId={order.id}
            cartLineIndex={radiusModal.cartLineIndex}
            accountNumber={order.account_number}
            existingUsername={radiusModal.existingUsername}
            defaultIPoEUsername={!radiusModal.existingUsername ? defaultBuid : null}
            onClose={() => setRadiusModal(null)}
            onSuccess={handleRadiusSuccess}
          />
        );
      })()}

      {accountServiceModal && (() => {
        const lineItem = provisioningSteps.find(
          (li) => li.cart_line_index === accountServiceModal.cartLineIndex,
        );
        const radiusStep = lineItem?.steps.find((s) => s.key === 'radius_credentials');
        const radiusUsername = radiusStep?.is_complete && radiusStep.notes ? radiusStep.notes : null;
        return (
          <AccountServiceModal
            open={!!accountServiceModal}
            orderId={order.id}
            cartLineIndex={accountServiceModal.cartLineIndex}
            accountId={order.account_id ? String(order.account_id) : null}
            serviceAddress={order.service_address}
            serviceTypeCode={lineItem?.service_type_code || ''}
            radiusUsername={radiusUsername}
            existingAccountServiceId={accountServiceModal.existingAccountServiceId ?? null}
            onClose={() => setAccountServiceModal(null)}
            onSuccess={handleAccountServiceSuccess}
          />
        );
      })()}

      {staticIpModal && (
        <StaticIpProvisionModal
          open={!!staticIpModal}
          orderId={order.id}
          cartLineIndex={staticIpModal.cartLineIndex}
          accountLinked={!!order.account_id}
          isComplete={staticIpModal.isComplete}
          onClose={() => setStaticIpModal(null)}
          onSuccess={handleAccountServiceSuccess}
        />
      )}

      {supaProvisionModal && (
        <SupaNetworksProvisionModal
          open={!!supaProvisionModal}
          orderId={order.id}
          cartLineIndex={supaProvisionModal.cartLineIndex}
          existingNotes={supaProvisionModal.existingNotes}
          onClose={() => setSupaProvisionModal(null)}
          onSuccess={handleSupaProvisionSuccess}
        />
      )}

      {/* Order Confirmation PDF Modal */}
      <Dialog
        open={!!confirmationPdfModal}
        onClose={() => {
          setConfirmationPdfModal(null);
          if (confirmationBlobUrlRef.current) {
            URL.revokeObjectURL(confirmationBlobUrlRef.current);
            confirmationBlobUrlRef.current = null;
          }
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Typography fontWeight={700}>{confirmationPdfModal?.label}</Typography>
          <IconButton onClick={() => setConfirmationPdfModal(null)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {confirmationPdfModal && (
            <Box
              component="iframe"
              src={confirmationPdfModal.src}
              sx={{ flex: 1, width: '100%', border: 'none', minHeight: 0 }}
              title={confirmationPdfModal.label}
            />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default OrderDetailPage;
